import { formatZipCode, cleanZipCode } from './zipCodeUtils';

export const CEP_PREFIXO_MAX_LENGTH = 5;
export const CEP_SUFIXO_MAX_LENGTH = 3;

export const parseCepParts = (cep: string): { cepPrefixo: string; cepSufixo: string } => {
  const digits = cep.replace(/\D/g, '');
  return {
    cepPrefixo: digits.slice(0, CEP_PREFIXO_MAX_LENGTH),
    cepSufixo: digits.slice(CEP_PREFIXO_MAX_LENGTH, CEP_PREFIXO_MAX_LENGTH + CEP_SUFIXO_MAX_LENGTH),
  };
};

export const limitCepPrefixo = (value: string): string =>
  value.replace(/\D/g, '').slice(0, CEP_PREFIXO_MAX_LENGTH);

export const limitCepSufixo = (value: string): string =>
  value.replace(/\D/g, '').slice(0, CEP_SUFIXO_MAX_LENGTH);

export const combineCepParts = (cepPrefixo: string, cepSufixo: string): string =>
  `${limitCepPrefixo(cepPrefixo)}${limitCepSufixo(cepSufixo)}`;

export const formatCepFieldValue = (value: string): string => formatZipCode(value);

export const getCepDigits = (cep: string): string => cleanZipCode(cep);

export const formatCepDisplay = (cep: string): string => {
  const { cepPrefixo, cepSufixo } = parseCepParts(cep);
  if (!cepPrefixo && !cepSufixo) {
    return '—';
  }
  if (!cepSufixo) {
    return cepPrefixo;
  }
  return `${cepPrefixo}-${cepSufixo}`;
};

/** Format CEP for editable field; empty remains empty (placeholder of the input). */
export const formatCepForInput = (cep: string): string => {
  const digits = getCepDigits(cep);
  if (!digits) {
    return '';
  }
  return formatZipCode(digits);
};

export const shouldFocusCepSufixo = (previousValue: string, nextValue: string): boolean =>
  limitCepPrefixo(previousValue).length < CEP_PREFIXO_MAX_LENGTH
  && limitCepPrefixo(nextValue).length === CEP_PREFIXO_MAX_LENGTH;

export const focusCepSufixoInput = (element: HTMLBsdsInputElement | null): void => {
  if (!element) {
    return;
  }

  if (typeof element.setFocus === 'function') {
    void element.setFocus();
    return;
  }

  (element.shadowRoot?.querySelector('input') as HTMLInputElement | null)?.focus();
};

export type CepFieldName = 'cepPrefixo' | 'cepSufixo';

export type CepFieldUpdate = {
  cepPrefixo?: string;
  cepSufixo?: string;
  shouldFocusSufixo?: boolean;
};

export const resolveCepFieldInput = (
  field: CepFieldName,
  rawValue: string,
  previousPrefixo: string,
): CepFieldUpdate => {
  if (field === 'cepPrefixo') {
    const cepPrefixo = limitCepPrefixo(rawValue);
    return {
      cepPrefixo,
      shouldFocusSufixo: shouldFocusCepSufixo(previousPrefixo, cepPrefixo),
    };
  }

  return {
    cepSufixo: limitCepSufixo(rawValue),
  };
};
